import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Logo from '../components/Logo';
import { usePageViewTracking, useTracking } from '../hooks/useTracking';
import { 
  ArrowRight, 
  Check, 
  Star,
  Package,
  Truck,
  ClipboardCheck,
  ShoppingCart,
  BarChart3,
  Clock,
  Shield,
  FileText,
  ClipboardList,
  CheckCircle2,
  TrendingUp,
  Users,
  ChefHat,
  Wine,
  UserCheck
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { trackEvent } = useTracking();
  
  // Track page view
  usePageViewTracking('landing');

  const features = [
    {
      icon: ShoppingCart,
      title: "Modèles de commandes",
      description: "Créez des modèles réutilisables pour vos commandes récurrentes. Un clic et votre panier est pré-rempli.",
      benefit: "Gagnez jusqu'à 80% de temps"
    },
    {
      icon: Package,
      title: "Multi-fournisseurs centralisé",
      description: "Gérez tous vos fournisseurs depuis une seule plateforme. Fini les fichiers Excel et les emails perdus.",
      benefit: "Tout au même endroit"
    },
    {
      icon: Truck,
      title: "Suivi des livraisons",
      description: "Suivez l'état de vos commandes en temps réel. Soyez alerté des livraisons à venir et des retards.",
      benefit: "Zéro oubli, zéro stress"
    },
    {
      icon: ClipboardCheck,
      title: "Contrôles HACCP",
      description: "Enregistrez vos contrôles à réception. Créez une preuve en cas de litige avec un fournisseur.",
      benefit: "Conformité garantie"
    }
  ];

  const plans = [
    {
      name: "Essai Gratuit",
      price: "0€",
      period: "14 jours",
      description: "Testez toutes les fonctionnalités sans engagement",
      features: [
        "Toutes les fonctionnalités",
        "Support email",
        "Commandes illimitées",
        "Export PDF"
      ],
      cta: "Commencer gratuitement",
      highlighted: false,
      badge: null
    },
    {
      name: "Pro",
      price: "39€",
      period: "/ mois",
      description: "Pour les restaurants qui veulent structurer leurs achats",
      features: [
        "Commandes illimitées",
        "Gestion fournisseurs",
        "Gestion d'équipes",
        "Check de livraison",
        "Tableau de bord analytics",
        "Validation hiérarchique"
      ],
      cta: "Choisir Pro",
      highlighted: true,
      badge: "Populaire"
    },
    {
      name: "Plus",
      price: "54€",
      period: "/ mois",
      description: "Pro + automatisation et personnalisation avancée",
      features: [
        "Tout du plan Pro",
        "Logo personnalisé sur PDFs",
        "Notifications avancées",
        "Rappels de commande",
        "Commandes récurrentes (modèles)",
        "Support premium"
      ],
      cta: "Choisir Plus",
      highlighted: false,
      badge: "Complet"
    }
  ];

  const testimonials = [
    {
      name: "Marie Dubois",
      role: "Gérante",
      restaurant: "Le Jardin Gourmand, Lyon",
      avatar: "M",
      comment: "OrderLab a transformé notre gestion des commandes. Je gagne 2h par semaine et je n'oublie plus jamais rien. Les modèles sont une révolution !",
      rating: 5
    },
    {
      name: "Thomas Martin",
      role: "Chef",
      restaurant: "La Table du Chef, Paris",
      avatar: "T",
      comment: "Fini les tableaux Excel compliqués. Avec OrderLab, je commande en 2 clics et j'ai toujours un historique clair. Le suivi HACCP est un vrai plus.",
      rating: 5
    },
    {
      name: "Sophie Leroux",
      role: "Propriétaire",
      restaurant: "Chez Sophie, Bordeaux",
      avatar: "S",
      comment: "La centralisation multi-fournisseurs est géniale. Plus besoin de jongler entre 10 emails et fichiers. Tout est au même endroit, c'est parfait.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F7F3E8] to-white">
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInDelayed {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounceSlow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes fill {
          from { width: 0; }
          to { width: 88%; }
        }
        .animate-fade-in {
          animation: fadeIn 0.8s ease-out forwards;
        }
        .animate-fade-in-delayed {
          animation: fadeInDelayed 0.8s ease-out 0.3s forwards;
          opacity: 0;
        }
        .animate-bounce-slow {
          animation: bounceSlow 2s ease-in-out infinite;
        }
        .animate-fill {
          animation: fill 1.5s ease-out forwards;
        }
      `}} />
      
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Logo size="default" />
            <Button 
              variant="outline" 
              onClick={() => navigate('/login')}
              data-testid="landing-login-button"
            >
              Se connecter
            </Button>
          </div>
        </div>
      </nav>

      {/* Trust Bar */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#1B4332]" />
              <span className="font-medium">500+ restaurants</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#1B4332]" />
              <span className="font-medium">10,000+ commandes/mois</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#1B4332]" />
              <span className="font-medium">95% de satisfaction</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#1B4332]" />
              <span className="font-medium">Conforme HACCP</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 sm:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-[#D8F3DC]/30 to-transparent pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text content */}
            <div className="text-center lg:text-left">
            <Badge className="mb-6 bg-[#D8F3DC] text-[#1B4332] border-[#1B4332]/20" variant="outline">
              🎉 Essai gratuit 14 jours sans carte bancaire
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Structurez vos achats,<br />
              <span className="text-[#1B4332]">fiabilisez vos réceptions</span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              La plateforme qui aide les restaurants indépendants à <strong>gagner du temps</strong> avec leurs fournisseurs, 
              <strong> centraliser leurs habitudes d'achat</strong> et <strong>créer des preuves</strong> en cas de litige.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center">
              <Button 
                size="lg" 
                className="bg-[#1B4332] hover:bg-[#152e22] text-white px-8 h-14 text-lg shadow-lg hover:shadow-xl transition-all"
                onClick={() => navigate('/register')}
                data-testid="landing-hero-cta"
              >
                Essai gratuit 14 jours
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="h-14 px-8 text-lg"
                onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
              >
                Voir les tarifs
              </Button>
            </div>

            <p className="text-sm text-gray-500 mt-6">
              ✓ Sans engagement &nbsp;•&nbsp; ✓ Sans carte bancaire &nbsp;•&nbsp; ✓ Configuration en 5 minutes
            </p>
          </div>
          
          {/* Right: iPad Mockup with real app */}
          <div className="relative">
            {/* iPad Frame */}
            <div className="relative mx-auto" style={{ maxWidth: '680px' }}>
              {/* iPad outer frame */}
              <div className="relative rounded-[2.5rem] bg-gradient-to-br from-gray-800 to-gray-900 p-3 shadow-2xl" style={{ aspectRatio: '4/3' }}>
                {/* iPad screen */}
                <div className="relative w-full h-full rounded-[2rem] bg-black overflow-hidden">
                  {/* Screenshot of real app - Catalog/Products */}
                  <div className="w-full h-full bg-white">
                    {/* Mock Catalog Content */}
                    <div className="h-full flex flex-col">
                      {/* Top bar */}
                      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8">
                            <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M14 8 L14 16 L10 28 C10 32 12 34 20 34 C28 34 30 32 30 28 L26 16 L26 8 Z" stroke="#1B4332" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                              <line x1="12" y1="8" x2="28" y2="8" stroke="#1B4332" strokeWidth="2.5" strokeLinecap="round"/>
                              <path d="M15 20 L18 24 L25 16" stroke="#1B4332" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            </svg>
                          </div>
                          <span className="font-bold text-gray-900">Commander</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-[#1B4332] text-white text-xs">3 dans le panier</Badge>
                        </div>
                      </div>
                      
                      {/* Supplier selector + Search */}
                      <div className="bg-gray-50 border-b border-gray-200 px-6 py-3 flex items-center gap-3">
                        <div className="text-xs text-gray-500">Fournisseur:</div>
                        <Badge variant="outline" className="text-xs">Boucherie Martin</Badge>
                      </div>
                      
                      {/* Products List */}
                      <div className="flex-1 bg-white p-6 overflow-hidden">
                        <div className="space-y-3">
                          {/* Product 1 */}
                          <div className="flex items-center gap-4 p-3 border border-gray-200 rounded-lg hover:border-[#1B4332]/30 transition-colors">
                            <div className="w-12 h-12 rounded-lg bg-[#D8F3DC] flex items-center justify-center flex-shrink-0">
                              <Package className="w-6 h-6 text-[#1B4332]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 text-sm">Bœuf haché</p>
                              <p className="text-xs text-gray-500">BH-001 • 1 kg</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="font-bold text-[#1B4332] text-sm">12,50 €</p>
                              <p className="text-xs text-gray-400">/kg</p>
                            </div>
                            <button className="px-3 py-1 bg-[#1B4332] text-white rounded text-xs font-medium hover:bg-[#152e22] transition-colors">
                              +
                            </button>
                          </div>

                          {/* Product 2 */}
                          <div className="flex items-center gap-4 p-3 border border-gray-200 rounded-lg hover:border-[#1B4332]/30 transition-colors">
                            <div className="w-12 h-12 rounded-lg bg-[#D8F3DC] flex items-center justify-center flex-shrink-0">
                              <Package className="w-6 h-6 text-[#1B4332]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 text-sm">Poulet fermier</p>
                              <p className="text-xs text-gray-500">PF-042 • 1 kg</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="font-bold text-[#1B4332] text-sm">9,80 €</p>
                              <p className="text-xs text-gray-400">/kg</p>
                            </div>
                            <button className="px-3 py-1 bg-[#1B4332] text-white rounded text-xs font-medium hover:bg-[#152e22] transition-colors">
                              +
                            </button>
                          </div>

                          {/* Product 3 */}
                          <div className="flex items-center gap-4 p-3 border border-gray-200 rounded-lg hover:border-[#1B4332]/30 transition-colors">
                            <div className="w-12 h-12 rounded-lg bg-[#D8F3DC] flex items-center justify-center flex-shrink-0">
                              <Package className="w-6 h-6 text-[#1B4332]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 text-sm">Entrecôte</p>
                              <p className="text-xs text-gray-500">EC-015 • 1 kg</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="font-bold text-[#1B4332] text-sm">29,90 €</p>
                              <p className="text-xs text-gray-400">/kg</p>
                            </div>
                            <button className="px-3 py-1 bg-[#1B4332] text-white rounded text-xs font-medium hover:bg-[#152e22] transition-colors">
                              +
                            </button>
                          </div>

                          {/* Product 4 */}
                          <div className="flex items-center gap-4 p-3 border border-gray-200 rounded-lg hover:border-[#1B4332]/30 transition-colors">
                            <div className="w-12 h-12 rounded-lg bg-[#D8F3DC] flex items-center justify-center flex-shrink-0">
                              <Package className="w-6 h-6 text-[#1B4332]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 text-sm">Saucisses</p>
                              <p className="text-xs text-gray-500">SC-028 • 500g</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="font-bold text-[#1B4332] text-sm">7,50 €</p>
                              <p className="text-xs text-gray-400">/500g</p>
                            </div>
                            <button className="px-3 py-1 bg-[#1B4332] text-white rounded text-xs font-medium hover:bg-[#152e22] transition-colors">
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* iPad home indicator */}
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-white/30 rounded-full"></div>
                </div>
                
                {/* iPad camera */}
                <div className="absolute top-6 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full bg-gray-700 border border-gray-600"></div>
              </div>
              
              {/* Floating badge - Fast ordering */}
              <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-2xl px-4 py-3 flex items-center gap-2 animate-bounce-slow z-10">
                <ShoppingCart className="w-4 h-4 text-[#1B4332]" />
                <span className="text-sm font-semibold text-gray-900">Commande rapide</span>
              </div>
              
              {/* Floating badge - Multi-supplier */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-2xl px-4 py-3 flex items-center gap-2 z-10">
                <Truck className="w-5 h-5 text-[#1B4332]" />
                <span className="text-sm font-semibold text-gray-900">Multi-fournisseurs</span>
              </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-[#D8F3DC] rounded-full blur-3xl opacity-50"></div>
            <div className="absolute -top-8 -left-8 w-40 h-40 bg-[#1B4332]/10 rounded-full blur-3xl"></div>
          </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#D8F3DC] text-[#1B4332]" variant="outline">
              Fonctionnalités
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Tout ce dont vous avez besoin
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Des outils pensés pour les restaurateurs qui veulent gagner du temps et sécuriser leurs approvisionnements
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-2 border-gray-100 hover:border-[#1B4332]/30 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#D8F3DC] flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-[#1B4332]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                        <p className="text-gray-600 mb-3">{feature.description}</p>
                        <Badge className="bg-[#1B4332]/10 text-[#1B4332] border-[#1B4332]/20" variant="outline">
                          {feature.benefit}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Interface Preview Section - with real component mockups */}
      <section className="py-20 bg-gradient-to-b from-white to-[#F7F3E8] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Success Story 1: Order Management */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
            <div className="order-2 lg:order-1">
              <Badge className="mb-4 bg-[#D8F3DC] text-[#1B4332]" variant="outline">
                Gestion des commandes
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Vous aussi, <span className="text-[#1B4332]">réussissez tous vos approvisionnements</span>
              </h2>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                Fini les oublis et les erreurs. Avec OrderLab, chaque commande est tracée, validée et sécurisée.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#1B4332] flex-shrink-0" />
                  <span className="text-gray-700">Commandes validées en temps réel</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#1B4332] flex-shrink-0" />
                  <span className="text-gray-700">PDF générés automatiquement</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#1B4332] flex-shrink-0" />
                  <span className="text-gray-700">Historique complet et traçable</span>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              {/* Mock Order Card - Validated */}
              <div className="relative animate-fade-in">
                <Card className="border-2 border-[#1B4332]/20 shadow-2xl hover:shadow-3xl transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-500">Commande</span>
                          <Badge className="bg-[#1B4332] text-white">#CMD-2847</Badge>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Boucherie Martin</h3>
                      </div>
                      <Badge className="bg-green-100 text-green-700 border-green-300">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Validée
                      </Badge>
                    </div>
                    
                    <div className="space-y-3 mb-4">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <div>
                          <p className="font-medium text-gray-900">Bœuf haché</p>
                          <p className="text-sm text-gray-500">10 kg</p>
                        </div>
                        <span className="font-semibold text-[#1B4332]">125,00 €</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <div>
                          <p className="font-medium text-gray-900">Poulet fermier</p>
                          <p className="text-sm text-gray-500">5 kg</p>
                        </div>
                        <span className="font-semibold text-[#1B4332]">62,50 €</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <div>
                          <p className="font-medium text-gray-900">Entrecôte</p>
                          <p className="text-sm text-gray-500">3 kg</p>
                        </div>
                        <span className="font-semibold text-[#1B4332]">89,70 €</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t-2 border-gray-200">
                      <span className="text-lg font-semibold text-gray-900">Total</span>
                      <span className="text-2xl font-bold text-[#1B4332]">277,20 €</span>
                    </div>
                    
                    <div className="mt-4 flex gap-2">
                      <Button size="sm" className="flex-1 bg-[#1B4332] hover:bg-[#152e22]">
                        <FileText className="w-4 h-4 mr-2" />
                        Télécharger PDF
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1">
                        <Truck className="w-4 h-4 mr-2" />
                        Suivi livraison
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Floating success badge */}
                <div className="absolute -top-4 -right-4 bg-white rounded-full shadow-xl p-3 animate-bounce-slow">
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Success Story 2: Analytics */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Mock Dashboard Stats */}
              <div className="space-y-4 animate-fade-in-delayed">
                <Card className="border-2 border-[#1B4332]/20 shadow-xl">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-[#D8F3DC] flex items-center justify-center">
                          <TrendingUp className="w-6 h-6 text-[#1B4332]" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Économies ce mois</p>
                          <p className="text-2xl font-bold text-[#1B4332]">-12%</p>
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-700">↓ Baisse</Badge>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full w-[88%] bg-[#1B4332] animate-fill"></div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-2 border-[#1B4332]/20 shadow-xl">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-[#D8F3DC] flex items-center justify-center">
                          <ClipboardList className="w-6 h-6 text-[#1B4332]" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Commandes ce mois</p>
                          <p className="text-2xl font-bold text-gray-900">47</p>
                        </div>
                      </div>
                      <Badge className="bg-blue-100 text-blue-700">+23% vs mois dernier</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            <div>
              <Badge className="mb-4 bg-[#D8F3DC] text-[#1B4332]" variant="outline">
                Analytics & Pilotage
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                <span className="text-[#1B4332]">Prenez les bonnes décisions</span> avec vos données
              </h2>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                Visualisez vos tendances d'achats, identifiez les économies possibles et optimisez vos coûts.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#1B4332] flex-shrink-0" />
                  <span className="text-gray-700">Dashboard temps réel</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#1B4332] flex-shrink-0" />
                  <span className="text-gray-700">Top produits et fournisseurs</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#1B4332] flex-shrink-0" />
                  <span className="text-gray-700">Tendances d'évolution des coûts</span>
                </div>
              </div>
            </div>
          </div>

          {/* Trust indicators */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="animate-fade-in">
              <div className="text-4xl font-bold text-[#1B4332] mb-2">80%</div>
              <div className="text-sm text-gray-600">Temps gagné en moyenne</div>
            </div>
            <div className="animate-fade-in-delayed">
              <div className="text-4xl font-bold text-[#1B4332] mb-2">500+</div>
              <div className="text-sm text-gray-600">Restaurants clients</div>
            </div>
            <div className="animate-fade-in">
              <div className="text-4xl font-bold text-[#1B4332] mb-2">10k+</div>
              <div className="text-sm text-gray-600">Commandes par mois</div>
            </div>
            <div className="animate-fade-in-delayed">
              <div className="text-4xl font-bold text-[#1B4332] mb-2">95%</div>
              <div className="text-sm text-gray-600">Taux de satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Management Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Visual mockup */}
            <div>
              <div className="relative">
                {/* Main management card */}
                <Card className="border-2 border-[#1B4332]/20 shadow-2xl">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-[#D8F3DC] flex items-center justify-center">
                          <Users className="w-6 h-6 text-[#1B4332]" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Gestion des équipes</h3>
                          <p className="text-sm text-gray-500">3 équipes actives</p>
                        </div>
                      </div>
                      <Badge className="bg-[#1B4332] text-white">Gérant</Badge>
                    </div>

                    {/* Team cards */}
                    <div className="space-y-3">
                      {/* Cuisine Team */}
                      <div className="bg-gradient-to-r from-[#D8F3DC] to-[#95D5B2]/30 rounded-xl p-4 border-2 border-[#1B4332]/10">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#1B4332] flex items-center justify-center">
                              <ChefHat className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">Cuisine</p>
                              <p className="text-xs text-gray-600">5 membres</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-xs bg-white border-[#1B4332]/20">
                            3 fournisseurs
                          </Badge>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <span className="text-xs px-2 py-1 bg-white/80 rounded-md text-gray-700">Boucherie Martin</span>
                          <span className="text-xs px-2 py-1 bg-white/80 rounded-md text-gray-700">Poissonnerie</span>
                          <span className="text-xs px-2 py-1 bg-white/80 rounded-md text-gray-700">Légumes Bio</span>
                        </div>
                      </div>

                      {/* Bar Team */}
                      <div className="bg-gradient-to-r from-[#D8F3DC] to-[#95D5B2]/30 rounded-xl p-4 border-2 border-[#1B4332]/10">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#1B4332] flex items-center justify-center">
                              <Wine className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">Bar</p>
                              <p className="text-xs text-gray-600">3 membres</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-xs bg-white border-[#1B4332]/20">
                            2 fournisseurs
                          </Badge>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <span className="text-xs px-2 py-1 bg-white/80 rounded-md text-gray-700">Vins & Spiritueux</span>
                          <span className="text-xs px-2 py-1 bg-white/80 rounded-md text-gray-700">Brasserie</span>
                        </div>
                      </div>

                      {/* Salle Team */}
                      <div className="bg-gradient-to-r from-[#D8F3DC] to-[#95D5B2]/30 rounded-xl p-4 border-2 border-[#1B4332]/10">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#1B4332] flex items-center justify-center">
                              <UserCheck className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">Salle</p>
                              <p className="text-xs text-gray-600">4 membres</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-xs bg-white border-[#1B4332]/20">
                            1 fournisseur
                          </Badge>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <span className="text-xs px-2 py-1 bg-white/80 rounded-md text-gray-700">Fournitures</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Floating badge */}
                <div className="absolute -top-4 -right-4 bg-white rounded-full shadow-xl p-3 animate-bounce-slow">
                  <Users className="w-8 h-8 text-[#1B4332]" />
                </div>
              </div>
            </div>

            {/* Right: Text content */}
            <div className="order-1 lg:order-2">
              <Badge className="mb-4 bg-[#D8F3DC] text-[#1B4332]" variant="outline">
                Gestion d'équipe
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                <span className="text-[#1B4332]">Déléguez intelligemment</span> avec la gestion d'équipes
              </h2>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                Organisez vos équipes (cuisine, salle, bar) et attribuez des accès spécifiques aux fournisseurs. 
                Chaque équipe commande auprès de ses fournisseurs assignés en toute autonomie.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#1B4332] flex-shrink-0" />
                  <span className="text-gray-700">Créez des équipes personnalisées</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#1B4332] flex-shrink-0" />
                  <span className="text-gray-700">Assignez des fournisseurs spécifiques</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#1B4332] flex-shrink-0" />
                  <span className="text-gray-700">Contrôlez les accès depuis un seul compte gérant</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#1B4332] flex-shrink-0" />
                  <span className="text-gray-700">Chaque équipe reste autonome dans ses commandes</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gradient-to-b from-[#F7F3E8] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#D8F3DC] text-[#1B4332]" variant="outline">
              Tarifs
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Choisissez votre plan
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Commencez gratuitement pendant 14 jours, puis choisissez le plan adapté à vos besoins
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <Card 
                key={plan.name || `plan-${index}`} 
                className={`relative ${plan.highlighted ? 'border-[#1B4332] border-2 shadow-xl scale-105' : 'border-gray-200'}`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-[#1B4332] text-white px-4 py-1">
                      {plan.badge}
                    </Badge>
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-[#1B4332]">{plan.price}</span>
                    <span className="text-gray-600 ml-1">{plan.period}</span>
                  </div>
                  <p className="text-gray-600 mb-6">{plan.description}</p>
                  
                  <Button 
                    className={`w-full h-12 mb-6 ${plan.highlighted ? 'bg-[#1B4332] hover:bg-[#152e22] text-white' : ''}`}
                    variant={plan.highlighted ? 'default' : 'outline'}
                    onClick={() => navigate('/register')}
                    data-testid={`landing-pricing-${plan.name.toLowerCase().replace(' ', '-')}-cta`}
                  >
                    {plan.cta}
                  </Button>

                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-[#1B4332] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#D8F3DC] text-[#1B4332]" variant="outline">
              Témoignages
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Ils nous font confiance
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Des centaines de restaurants utilisent déjà OrderLab pour gagner du temps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border border-gray-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">"{testimonial.comment}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#D8F3DC] flex items-center justify-center">
                      <span className="text-[#1B4332] font-semibold text-lg">{testimonial.avatar}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">{testimonial.role} • {testimonial.restaurant}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#1B4332] to-[#0D2E1F] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Prêt à transformer votre gestion des commandes ?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Rejoignez des centaines de restaurants qui ont déjà simplifié leurs approvisionnements
          </p>
          <Button 
            size="lg" 
            className="bg-white text-[#1B4332] hover:bg-gray-100 px-8 h-14 text-lg shadow-xl hover:shadow-2xl transition-all"
            onClick={() => {
              trackEvent('cta_click', { cta_location: 'final_cta', cta_text: 'Commencer mon essai gratuit' });
              navigate('/register');
            }}
            data-testid="landing-final-cta"
          >
            Commencer mon essai gratuit
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="text-sm text-white/70 mt-6">
            14 jours gratuits • Sans carte bancaire • Sans engagement
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <Logo size="default" />
              <p className="text-gray-600 text-sm mt-4">
                La solution SaaS pour structurer vos achats restaurant
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Produit</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="#" className="hover:text-[#1B4332]">Fonctionnalités</a></li>
                <li><a href="#pricing" className="hover:text-[#1B4332]">Tarifs</a></li>
                <li><a href="#" className="hover:text-[#1B4332]">Démo</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="#" className="hover:text-[#1B4332]">Centre d'aide</a></li>
                <li><a href="#" className="hover:text-[#1B4332]">Contact</a></li>
                <li><a href="#" className="hover:text-[#1B4332]">Documentation</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Légal</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="#" className="hover:text-[#1B4332]">CGU</a></li>
                <li><a href="#" className="hover:text-[#1B4332]">Confidentialité</a></li>
                <li><a href="#" className="hover:text-[#1B4332]">Mentions légales</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-200 mt-8 pt-8 text-center text-sm text-gray-600">
            <p>© 2024 OrderLab - Tous droits réservés</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
